import React, { useState } from "react";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Input from "@material-ui/core/Input";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { changePassword } from "@State/index";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import ButtonSave from "@View/components/buttons/ButtonSave";
import { getUserId } from "@Security/stateHelpers";
import ModalTittle from "@View/components/ModalTittle";
import "@View/components/formInputs/inputs.css";
import { isValidPassword, MIN_PASSWORD_LENGTH } from "../../../security";

// redux
const mapDispatchToProps = (dispatch) => ({
  changePassword: (body, history, errorText) =>
    dispatch(changePassword(body, history, errorText)),
});

const cardBox = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "#FEFFFF",
  outline: "none",
  borderRadius: "9px",
  maxWidth: "400px",
  width: "100%",
  padding: "40px 40px 36px",
  boxSizing: "border-box",
};

const cancelLinkStyle = {
  textAlign: "center",
  marginTop: "14px",
  color: "#6FA4F2",
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: 500,
  cursor: "pointer",
};

const validationErrorStyle = {
  color: "#f44336",
  fontSize: "12px",
  lineHeight: "16px",
  marginTop: "4px",
};

const validationHintStyle = {
  fontSize: "12px",
  lineHeight: "16px",
  marginTop: "4px",
};

const getValidationHintColor = (isValid, value) => {
  if (!value) return "#9e9e9e";
  return isValid ? "#4caf50" : "#f44336";
};

// Per-requirement status for UI hints (reuses constants from security.js)
const getPasswordStatus = (password) => ({
  hasMinLength: password.length >= MIN_PASSWORD_LENGTH,
  hasUppercase: /\p{Lu}/u.test(password),
  hasDigit: /\d/.test(password),
});

// Locally styled password field matching the app's PassInput design
const PasswordField = ({ label, value, onChange }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FormControl className="PassInput">
      <InputLabel>{label}</InputLabel>
      <Input
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
      />
    </FormControl>
  );
};

PasswordField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

const ChangePasswordPage = ({ changePassword }) => {
  const { t } = useTranslation();
  let history = useHistory();
  const [body, setBody] = useState({
    id: getUserId(),
    oldpassword: "",
    newpassword: "",
    confirmPassword: "",
  });

  const validation = getPasswordStatus(body.newpassword);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!body.oldpassword || !body.newpassword || !body.confirmPassword) {
      alert(t("FieldRequired"));
      return;
    }
    if (!isValidPassword(body.newpassword)) {
      alert(t("passReqError"));
      return;
    }
    if (body.newpassword !== body.confirmPassword) {
      alert(t("PasswordMismatch"));
      return;
    }
    changePassword(body, history, t("ChangePassError"));
  };

  const handleCancel = (e) => {
    e.preventDefault();
    history.goBack();
  };

  return (
    <div style={cardBox}>
      <ModalTittle marginBottom="24px">{t("Change Password")}</ModalTittle>
      <form onSubmit={handleSubmit}>
        <PasswordField
          label={t("Old password")}
          value={body.oldpassword}
          onChange={(value) => setBody({ ...body, oldpassword: value })}
        />
        <PasswordField
          label={t("New password")}
          value={body.newpassword}
          onChange={(value) => setBody({ ...body, newpassword: value })}
        />

        <PasswordField
          label={t("Confirm password")}
          value={body.confirmPassword}
          onChange={(value) => setBody({ ...body, confirmPassword: value })}
        />
        <div style={{ marginTop: "4px", marginBottom: "10px" }}>
          <div style={{ ...validationHintStyle, color: getValidationHintColor(validation.hasMinLength, body.newpassword) }}>
            • {t("passReqMinLength")}
          </div>
          <div style={{ ...validationHintStyle, color: getValidationHintColor(validation.hasUppercase, body.newpassword) }}>
            • {t("passReqUppercase")}
          </div>
          <div style={{ ...validationHintStyle, color: getValidationHintColor(validation.hasDigit, body.newpassword) }}>
            • {t("passReqDigit")}
          </div>
          <div style={{ ...validationHintStyle, color: getValidationHintColor(body.confirmPassword ? body.newpassword === body.confirmPassword : true, body.confirmPassword) }}>
            • {t("PasswordMismatch")}
          </div>
        </div>
        <ButtonSave marginTop="28px" marginBottom="0px">
          buttons.Ok
        </ButtonSave>
      </form>
      <div style={cancelLinkStyle} onClick={handleCancel}>
        {t("buttons.Cancel")}
      </div>
    </div>
  );
};

ChangePasswordPage.propTypes = {
  changePassword: PropTypes.func.isRequired,
};

export default connect(null, mapDispatchToProps)(ChangePasswordPage);
